import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getConnections, getChats, createChat, deleteChat, deleteConnection } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { storage } from '../utils/storage';
import ConnectModal from './ConnectModal';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faComments,
  faDatabase,
  faFileLines,
  faMoon,
  faPlug,
  faRightFromBracket,
  faSun,
  faUser,
  faTable,
  faXmark,
  faTrashCan,
  faCircleNodes,
  
} from '@fortawesome/free-solid-svg-icons';

const getConnectionIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'csv') return faFileLines;
  if (t === 'excel') return faTable;
  return faDatabase;
};

const getConnectionColor = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'postgres') return '#3b82f6';
  if (t === 'mysql') return '#f59e0b';
  if (t === 'oracle') return '#ef4444';
  if (t === 'sap_hana') return '#8b5cf6';
  if (t === 'csv') return '#16a34a';
  return '#7c3aed';
};

export default function Sidebar({
  activeChat,
  onSelectChat,
  onNewChat,
  activeConnection,
  onSelectConnection,
  activeMode,
  onStartRelation,
  relationSessions = [],
  relationChats = [],
  activeRelationSession,
  activeRelationChat,
  expandedRelationSession,
  onSelectRelationSession,
  onSelectRelationChat,
  onNewRelationChat,
  onDeleteRelationSession,
  onDeleteRelationChat,
  onSetExpandedRelationSession,
  pendingConnection,
  onConsumePendingConnection,
  autoOpenConnectModal,
  onConsumeAutoOpenConnectModal,
}) {
  const { user, logout } = useAuth();
  // user now comes from JWT /auth/me
  const { toggle, isDark } = useTheme();
  const [connections, setConnections] = useState([]);
  const [connectionLoadState, setConnectionLoadState] = useState({
    loading: true,
    error: null,
    empty: false,
  });
  const [chats, setChats] = useState([]);
  const [showConnectModal, setShowConnectModal] = useState(false);

  useEffect(() => {
    if (!autoOpenConnectModal) return;
    setShowConnectModal(true);
    onConsumeAutoOpenConnectModal?.();
  }, [autoOpenConnectModal, onConsumeAutoOpenConnectModal]);
  const [expandedConn, setExpandedConn] = useState(null);
  const [loadingChats, setLoadingChats] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hoveredConnId, setHoveredConnId] = useState(null);
  const [hoveredRelationSessionId, setHoveredRelationSessionId] = useState(null);
  const loadConnections = useCallback(async () => {
    setConnectionLoadState({ loading: true, error: null, empty: false });
    try {
      const data = await getConnections();
      const list = Array.isArray(data) ? data : [];
      setConnections(list);
      setConnectionLoadState({
        loading: false,
        error: null,
        empty: list.length === 0,
      });
      // Restore last active connection
      const saved = storage.get('ds_active_connection');
      if (saved && list.find(c => c.id === saved)) {
        const conn = list.find(c => c.id === saved);
        onSelectConnection(conn);
        setExpandedConn(saved);
      }
    } catch (error) {
      setConnections([]);
      setConnectionLoadState({
        loading: false,
        error: error?.message || 'Failed to load connections',
        empty: false,
      });
      toast.error('Failed to load connections');
    }
  }, [onSelectConnection]);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const loadChats = useCallback(async (connId) => {
    if (!connId) return;
    setLoadingChats(true);
    try {
      const data = await getChats(connId);
      setChats(data);
      // Restore last active chat
      const savedChat = storage.get('ds_active_chat');
      if (savedChat && data.find(c => c.id === savedChat)) {
        onSelectChat(data.find(c => c.id === savedChat));
      }
    } catch { toast.error('Failed to load chats'); }
    finally { setLoadingChats(false); }
  }, [onSelectChat]);

  useEffect(() => {
    if (activeConnection) loadChats(activeConnection.id);
    else setChats([]);
  }, [activeConnection, loadChats]);

  const handleSelectConnection = (conn) => {
    onSelectConnection(conn);
    setExpandedConn(conn.id);
    storage.set('ds_active_connection', conn.id);
    onSelectChat(null);
    storage.remove('ds_active_chat');
  };

  const handleNewChat = async () => {
    if (!activeConnection) return toast.error('Select a connection first');
    try {
      const chat = await createChat({ connection_id: activeConnection.id, title: 'New Chat' });
      setChats(prev => [chat, ...prev]);
      onSelectChat(chat);
      onNewChat && onNewChat(chat);
      storage.set('ds_active_chat', chat.id);
    } catch { toast.error('Failed to create chat'); }
  };
//new changes show
  const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation();
    try {
      await deleteChat(chatId);
      setChats(prev => prev.filter(c => c.id !== chatId));
      if (activeChat?.id === chatId) { onSelectChat(null); storage.remove('ds_active_chat'); }
      toast.success('Chat deleted');
    } catch { toast.error('Failed to delete chat'); }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === 'connection') {
        await deleteConnection(confirmDelete.id);
        setConnections(prev => prev.filter(c => c.id !== confirmDelete.id));
        if (activeConnection?.id === confirmDelete.id) {
          onSelectConnection(null);
          onSelectChat(null);
          storage.remove('ds_active_connection');
          storage.remove('ds_active_chat');
        }
        toast.success('Connection deleted');
      } else if (confirmDelete.kind === 'relation-session') {
        await onDeleteRelationSession?.(confirmDelete.id);
        toast.success('Relation session deleted');
      }
    } catch {
      toast.error(confirmDelete.kind === 'connection' ? 'Failed to delete connection' : 'Failed to delete relation session');
    } finally {
      setConfirmDelete(null);
    }
  };

  const relationSessionDeleteHandler = (sessionId) => {
    setConfirmDelete({
      kind: 'relation-session',
      id: sessionId,
      title: 'Delete relation session?',
    });
  };

  const connectionDeleteHandler = (e, connId) => {
    e.stopPropagation();
    setConfirmDelete({
      kind: 'connection',
      id: connId,
      title: 'Delete data source?',
    });
  };

  const closeConfirm = () => setConfirmDelete(null);

  const handleConnected = (conn) => {
    setConnections(prev => [conn, ...prev]);
    handleSelectConnection(conn);
  };

  // Handoff from /connect/:type after "Start Chat" — selects the freshly
  // uploaded connection and creates its first chat directly off the passed
  // connection object (not the `activeConnection` prop, which hasn't
  // re-rendered yet in the same tick), then hands control back to the
  // caller to clear the router state so a refresh doesn't replay it.
  const consumedPendingIdRef = useRef(null);
  useEffect(() => {
    if (!pendingConnection || consumedPendingIdRef.current === pendingConnection.id) return;
    consumedPendingIdRef.current = pendingConnection.id;

    setConnections(prev => [pendingConnection, ...prev.filter(c => c.id !== pendingConnection.id)]);
    handleSelectConnection(pendingConnection);

    createChat({ connection_id: pendingConnection.id, title: 'New Chat' })
      .then(chat => {
        setChats(prev => [chat, ...prev]);
        onSelectChat(chat);
        storage.set('ds_active_chat', chat.id);
      })
      .catch(() => toast.error('Failed to create chat'))
      .finally(() => onConsumePendingConnection && onConsumePendingConnection());
  }, [pendingConnection]);

  const chatsByConn = chats.filter(c => c.connection_id === activeConnection?.id);
  const relationChatsBySession = relationChats.filter(c => c.relation_session_id === activeRelationSession?.id);

  return (
    <>
      <aside style={s.sidebar}>
        {/* Header */}
        <div style={s.header}>
         <div style={s.logo}>
  <img src="/logo-blue.png" alt="Logo" style={s.logoImg} />
  <span style={s.logoText}>DataSphere</span>
</div>
          <div style={s.headerActions}>
            <button onClick={toggle} style={s.iconBtn} title="Toggle theme">
              <FontAwesomeIcon icon={isDark ? faSun : faMoon} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* New Connection */}
        <div style={s.section}>
          <button onClick={() => setShowConnectModal(true)} style={s.newConnBtn}>
            <span style={s.plusIcon}>+</span>
            New Connection
          </button>
          <button onClick={onStartRelation} style={{ ...s.relationBtn, ...(activeMode === 'relation' ? s.relationBtnActive : {}) }}>
            <span style={s.plusIcon}>+</span>
            Relation Connect
          </button>
        </div>

        {/* Connections List */}
        <div style={s.sectionLabel}>DATA SOURCES</div>
        <div style={s.connList}>
          {connectionLoadState.loading && (
            <div style={s.empty}>
              <span style={s.spinner} />
              <span>Loading connections…</span>
            </div>
          )}

          {!connectionLoadState.loading && connectionLoadState.error && (
            <div style={s.empty}>
              <FontAwesomeIcon icon={faCircleNodes} style={{ fontSize: 28 }} aria-hidden="true" />
              <span>{connectionLoadState.error}</span>
              <button onClick={loadConnections} style={s.retryBtn}>Retry</button>
            </div>
          )}

          {!connectionLoadState.loading && !connectionLoadState.error && connectionLoadState.empty && (
            <div style={s.empty}>
              <div style={s.emptyIconWrap}>
                <FontAwesomeIcon icon={faCircleNodes} style={{ fontSize: 34 }} aria-hidden="true" />
              </div>
              <strong style={s.emptyTitle}>No connections yet</strong>
              <span style={s.emptyDesc}>Create your first database or file connection to start exploring data.</span>
            </div>
          )}

          {!connectionLoadState.loading && !connectionLoadState.error && connections.map(conn => (
            <div key={conn.id}>
              <div
                style={{
                  ...s.connItem,
                  ...(activeConnection?.id === conn.id ? s.connItemActive : {}),
                }}
                onClick={() => handleSelectConnection(conn)}
                onMouseEnter={() => setHoveredConnId(conn.id)}
                onMouseLeave={() => setHoveredConnId(null)}
              >
                <span style={{ ...s.connTypeTag, background: getConnectionColor(conn.type) + '22', color: getConnectionColor(conn.type) }}>
                  <FontAwesomeIcon icon={getConnectionIcon(conn.type)} aria-hidden="true" />
                </span>
                <span style={s.connName}>{conn.name}</span>
                <div style={{ ...s.connActions, opacity: hoveredConnId === conn.id ? 1 : 0 }}>
                  <button
                    onClick={e => { e.stopPropagation(); setExpandedConn(expandedConn === conn.id ? null : conn.id); }}
                    style={s.microBtn} title="Toggle chats"
                  >
                    <FontAwesomeIcon icon={expandedConn === conn.id ? faChevronDown : faChevronRight} aria-hidden="true" />
                  </button>
                  <button onClick={e => connectionDeleteHandler(e, conn.id)} style={{ ...s.microBtn, color: 'var(--error)' }} title="Delete data source">
                    <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Chats under connection */}
              {expandedConn === conn.id && activeConnection?.id === conn.id && (
                <div style={s.chatList}>
                  <button onClick={handleNewChat} style={s.newChatBtn}>
                    + New Chat
                  </button>
                  {loadingChats && <div style={s.chatLoading}><span style={s.dot} /><span style={s.dot} /><span style={s.dot} /></div>}
                  {chatsByConn.map(chat => (
                    <div
                      key={chat.id}
                      style={{ ...s.chatItem, ...(activeChat?.id === chat.id ? s.chatItemActive : {}) }}
                      onClick={() => { onSelectChat(chat); storage.set('ds_active_chat', chat.id); }}
                    >
                      <span style={s.chatIcon}><FontAwesomeIcon icon={faComments} aria-hidden="true" /></span>
                      <span style={s.chatTitle}>{chat.title}</span>
                      <button onClick={e => handleDeleteChat(e, chat.id)} style={s.microBtn} title="Delete chat">
                        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {!loadingChats && chatsByConn.length === 0 && (
                    <div style={s.noChats}>No chats yet - ask something!</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Relations */}
        <div style={s.sectionLabel}>RELATIONS</div>
        <div style={s.connList}>
          {relationSessions.length === 0 && (
            <div style={s.empty}>
              <FontAwesomeIcon icon={faTable} style={{ fontSize: 28 }} aria-hidden="true" />
              <span>No relation sessions yet</span>
            </div>
          )}
          {relationSessions.map(session => (
            <div key={session.id}>
              <div
                style={{
                  ...s.connItem,
                  ...(activeRelationSession?.id === session.id ? s.connItemActive : {}),
                }}
                onClick={() => onSelectRelationSession && onSelectRelationSession(session)}
                onMouseEnter={() => setHoveredRelationSessionId(session.id)}
                onMouseLeave={() => setHoveredRelationSessionId(null)}
              >
                <span style={{ ...s.connTypeTag, background: 'var(--accent-subtle)', color: 'var(--accent)' }}>
                  <FontAwesomeIcon icon={faTable} aria-hidden="true" />
                </span>
                <span style={s.connName}>{session.name}</span>
                <div style={{ ...s.connActions, opacity: hoveredRelationSessionId === session.id ? 1 : 0 }}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onSetExpandedRelationSession && onSetExpandedRelationSession(
                        expandedRelationSession === session.id ? null : session.id
                      );
                    }}
                    style={s.microBtn}
                    title="Toggle chats"
                  >
                    <FontAwesomeIcon icon={expandedRelationSession === session.id ? faChevronDown : faChevronRight} aria-hidden="true" />
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      relationSessionDeleteHandler(session.id);
                    }}
                    style={{ ...s.microBtn, color: 'var(--error)' }}
                    title="Delete relation session"
                  >
                    <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                  </button>
                </div>
              </div>

              {expandedRelationSession === session.id && activeRelationSession?.id === session.id && (
                <div style={s.chatList}>
                  <button onClick={onNewRelationChat} style={s.newChatBtn}>
                    + New Chat
                  </button>
                  {relationChatsBySession.map(chat => (
                    <div
                      key={chat.id}
                      style={{ ...s.chatItem, ...(activeRelationChat?.id === chat.id ? s.chatItemActive : {}) }}
                      onClick={() => onSelectRelationChat && onSelectRelationChat(chat)}
                    >
                      <span style={s.chatIcon}><FontAwesomeIcon icon={faComments} aria-hidden="true" /></span>
                      <span style={s.chatTitle}>{chat.title}</span>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onDeleteRelationChat && onDeleteRelationChat(chat.id);
                        }}
                        style={s.microBtn}
                        title="Delete chat"
                      >
                        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  {relationChatsBySession.length === 0 && (
                    <div style={s.noChats}>No chats yet - ask something!</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* User footer */}
        <div style={s.footer}>
          <div style={s.userInfo}>
            <div style={s.avatar}><FontAwesomeIcon icon={faUser} aria-hidden="true" /></div>
            <div style={s.userDetails}>
              <span style={s.userName}>{user?.name}</span>
              <span style={s.userEmail}>{user?.email}</span>
            </div>
          </div>
          <button onClick={logout} style={s.logoutBtn} title="Sign out">
            <FontAwesomeIcon icon={faRightFromBracket} aria-hidden="true" />
          </button>
        </div>
      </aside>

      {showConnectModal && (
        <ConnectModal onClose={() => setShowConnectModal(false)} onCreated={handleConnected} />
      )}
      <ConfirmModal
        open={!!confirmDelete}
        title={confirmDelete?.title || 'Confirm delete'}
        description="Are you sure you want to delete this? All associated chats, data, and relations will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete Permanently"
        cancelLabel="Cancel"
        onCancel={closeConfirm}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

const s = {
  sidebar: {
    width: 'var(--sidebar-w)', flexShrink: 0, height: '100vh',
    background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-subtle)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    animation: 'fadeInLeft 0.3s ease',
  },
  header: {
    padding: '16px 16px 12px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)',
  },
 logo: {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
},

logoImg: {
  width: 80,
  height: 40,
  objectFit: 'contain',
},
  logoMark: { fontSize: 20, color: 'var(--accent)' },
  logoText: { fontSize: 16, fontWeight: 700, color: '#2563eb', letterSpacing: '-0.3px' },
  headerActions: { display: 'flex', gap: 4 },
  iconBtn: {
    width: 32, height: 32, borderRadius: 8, background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
  },
  section: { padding: '12px 12px 4px' },
  newConnBtn: {
    width: '100%', padding: '9px 12px', borderRadius: 10,
    background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 13,
    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
    gap: 8, transition: 'all 0.2s', fontFamily: 'var(--font-main)',
  },
  relationBtn: {
    width: '100%', padding: '9px 12px', borderRadius: 10,
    background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13,
    border: '1px solid var(--border-subtle)', cursor: 'pointer', display: 'flex', alignItems: 'center',
    gap: 8, transition: 'all 0.2s', fontFamily: 'var(--font-main)', marginTop: 8,
  },
  relationBtnActive: { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent) inset' },
  plusIcon: { fontSize: 18, fontWeight: 300, lineHeight: 1 },
  sectionLabel: {
    padding: '12px 16px 6px', fontSize: 10, fontWeight: 700,
    color: 'var(--text-muted)', letterSpacing: '0.8px',
  },
  connList: { flex: 1, overflowY: 'auto', padding: '0 8px' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    padding: '32px 16px', color: 'var(--text-muted)', fontSize: 12,
    textAlign: 'center',
  },
  emptyIconWrap: {
    width: 58,
    height: 58,
    borderRadius: 18,
    background: 'var(--accent-subtle)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: { fontSize: 14, color: 'var(--text-primary)', fontWeight: 800 },
  emptyDesc: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 210 },
  spinner: {
    width: 16, height: 16, border: '2px solid var(--border-subtle)',
    borderTopColor: 'var(--accent)', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
  retryBtn: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  connItem: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
    borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
    marginBottom: 2,
  },
  connItemActive: { background: 'var(--bg-active)' },
  connTypeTag: {
    width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 14, flexShrink: 0,
  },
  connName: { flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  connActions: { display: 'flex', gap: 2, opacity: 0, transition: 'opacity 0.15s' },
  microBtn: {
    width: 22, height: 22, borderRadius: 5, background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  chatList: { paddingLeft: 12, paddingBottom: 8, display: 'flex', flexDirection: 'column', gap: 2 },
  newChatBtn: {
    padding: '6px 10px', borderRadius: 8, border: '1px dashed var(--border-subtle)',
    background: 'none', color: 'var(--accent)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', textAlign: 'left', marginBottom: 4, transition: 'all 0.2s',
    fontFamily: 'var(--font-main)',
  },
  chatItem: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px',
    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
  },
  chatItemActive: { background: 'var(--accent-subtle)' },
  chatIcon: { fontSize: 12, flexShrink: 0 },
  chatTitle: { flex: 1, fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chatLoading: { display: 'flex', gap: 4, padding: '8px 10px', alignItems: 'center' },
  dot: {
    width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
    animation: 'pulse 1.2s ease-in-out infinite',
    display: 'inline-block',
  },
  noChats: { fontSize: 11, color: 'var(--text-muted)', padding: '4px 10px', fontStyle: 'italic' },
  footer: {
    padding: '12px 14px', borderTop: '1px solid var(--border-subtle)',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  userInfo: { display: 'flex', alignItems: 'center', gap: 9, flex: 1, overflow: 'hidden' },
  avatar: {
    width: 32, height: 32, borderRadius: 10, background: 'var(--accent)',
    color: '#fff', fontWeight: 700, fontSize: 14, display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  userDetails: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  userName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userEmail: { fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: {
    width: 30, height: 30, borderRadius: 8, background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
};
